import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, PencilSimple, Trash, Gauge, Heart } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { EditAttributeDialog } from './EditAttributeDialog'

interface GameAttribute {
  id: string
  name: string
  description: string
  min: number
  max: number
  default: number
}

interface AttributesTabProps {
  tableId: string
  attributes: GameAttribute[]
  isGameMaster: boolean
  onAttributesChange: () => void
  onSaveAttribute: (attributeData: Omit<GameAttribute, 'id'> & { id?: string }) => Promise<void>
  onDeleteAttribute: (attributeId: string) => Promise<void>
  /** HP inicial da mesa (fase 3 do plano de feedback; default 20) */
  initialHp?: number
  /** Salvar o HP inicial (só mestre; ausente esconde o card) */
  onSaveInitialHp?: (initialHp: number) => Promise<void>
}

export function AttributesTab({
  tableId,
  attributes,
  isGameMaster,
  onAttributesChange,
  onSaveAttribute,
  onDeleteAttribute,
  initialHp = 20,
  onSaveInitialHp,
}: AttributesTabProps) {
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingAttribute, setEditingAttribute] = useState<GameAttribute | null>(null)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const [hpDraft, setHpDraft] = useState(String(initialHp))
  const [savingHp, setSavingHp] = useState(false)

  // O game_config carrega depois do mount — sincronizar o rascunho
  useEffect(() => {
    setHpDraft(String(initialHp))
  }, [initialHp])

  const hpValido = /^\d+$/.test(hpDraft) && parseInt(hpDraft, 10) >= 1
  const handleSaveHp = async () => {
    if (!onSaveInitialHp || !hpValido) return
    setSavingHp(true)
    try {
      await onSaveInitialHp(parseInt(hpDraft, 10))
      toast.success('HP inicial atualizado')
      onAttributesChange()
    } catch (error: any) {
      console.error('Erro ao salvar HP inicial:', error)
      toast.error(error.response?.data?.detail || error.message || 'Erro ao salvar HP inicial')
    } finally {
      setSavingHp(false)
    }
  }

  const handleCreateAttribute = () => {
    setEditingAttribute(null)
    setShowEditDialog(true)
  }

  const handleEditAttribute = (attributeData: GameAttribute) => {
    setEditingAttribute(attributeData)
    setShowEditDialog(true)
  }

  const handleDeleteAttribute = async (attributeId: string, attributeName: string) => {
    if (
      !confirm(
        `Tem certeza que deseja excluir o atributo "${attributeName}"?\n\n` +
          'Personagens existentes mantêm os valores já distribuídos, mas a ficha deixa de exibi-los.\n' +
          'Esta ação não pode ser desfeita.',
      )
    ) {
      return
    }

    setIsDeleting(attributeId)
    try {
      await onDeleteAttribute(attributeId)
      toast.success(`Atributo "${attributeName}" excluído com sucesso`)
      onAttributesChange()
    } catch (error: any) {
      console.error('Erro ao excluir atributo:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao excluir atributo'
      toast.error(errorMessage)
    } finally {
      setIsDeleting(null)
    }
  }

  const handleSaveAttribute = async (attributeData: Omit<GameAttribute, 'id'> & { id?: string }) => {
    await onSaveAttribute(attributeData)
    toast.success(editingAttribute ? 'Atributo atualizado com sucesso' : 'Atributo criado com sucesso')
    onAttributesChange()
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Atributos da Campanha</h3>
          <p className="text-sm text-muted-foreground">
            Defina as características que os personagens distribuem pontos
          </p>
        </div>
        {isGameMaster && (
          <Button onClick={handleCreateAttribute}>
            <Plus size={16} className="mr-2" />
            Novo Atributo
          </Button>
        )}
      </div>

      {/* HP inicial da mesa (fase 3): vale para os proximos personagens */}
      {isGameMaster && onSaveInitialHp && (
        <Card className="mb-4">
          <CardContent className="flex flex-wrap items-end gap-3 py-4">
            <div className="flex items-center gap-2">
              <Heart size={20} className="text-rose-500" />
              <div>
                <Label htmlFor="initial-hp" className="font-semibold">
                  HP inicial dos personagens
                </Label>
                <p className="text-xs text-muted-foreground">
                  Vale para os próximos personagens; os existentes não mudam.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="initial-hp"
                type="number"
                min={1}
                max={9999}
                value={hpDraft}
                onChange={e => setHpDraft(e.target.value)}
                className="w-24"
              />
              <Button
                size="sm"
                onClick={handleSaveHp}
                disabled={!hpValido || savingHp || parseInt(hpDraft, 10) === initialHp}
              >
                Salvar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {attributes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Gauge size={32} className="text-muted-foreground" />
            </div>
            <h4 className="text-lg font-medium mb-2">Nenhum atributo configurado</h4>
            <p className="text-muted-foreground mb-4 max-w-md">
              {isGameMaster
                ? 'Crie atributos para os personagens da campanha (ex.: Corpo, Mente, Espírito).'
                : 'O mestre ainda não configurou atributos para esta campanha.'}
            </p>
            {isGameMaster && (
              <Button onClick={handleCreateAttribute}>
                <Plus size={16} className="mr-2" />
                Criar Primeiro Atributo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {attributes.map((attributeData) => (
            <Card key={attributeData.id} className="group hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{attributeData.name}</CardTitle>
                <Badge variant="secondary" className="mt-1 w-fit font-mono text-xs">
                  {attributeData.min} – {attributeData.max} · inicial {attributeData.default}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {attributeData.description && (
                  <CardDescription className="line-clamp-3">{attributeData.description}</CardDescription>
                )}

                {isGameMaster && (
                  <div className="flex gap-2 pt-2 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEditAttribute(attributeData)}
                    >
                      <PencilSimple size={14} className="mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteAttribute(attributeData.id, attributeData.name)}
                      disabled={isDeleting === attributeData.id}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EditAttributeDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        attributeData={editingAttribute}
        onSave={handleSaveAttribute}
      />
    </>
  )
}
